import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import React, { ReactNode, type JSX } from 'react'

import { useSnackbar } from '@context/SnackbarContext'

import { Body2, Title2 } from '@ui/Typography/Typography'
import { Button, ButtonProps, IconButton } from '@ui/components/Button'
import { TextColorType } from '@ui/design-system/colors/colors'
import { CloseIcon } from '@ui/icons/Close.icon'
import { InfoFilledIcon } from '@ui/icons/InfoFilled.icon'

import { useWindowResize } from '@utils/domUtils'

import styles from './Snackbar.module.scss'

export type SnackbarType = 'success' | 'info' | 'warning' | 'error' | 'generic'

export interface SnackbarData {
  message: string
  type?: SnackbarType
  /**
   * @deprecated Use `message` instead. This prop is kept only for legacy cases
   * where rich content needs to be rendered.
   */
  content?: ReactNode
  id: string
  noTimeout?: boolean
  closable?: boolean
  ignoreMessageLength?: boolean
  action?: {
    label: string
    onClick: () => void
  }
  subTitle?: string
}

const Snackbar: React.FC<SnackbarData> = (props) => {
  const { id, type = 'generic', message, closable, action, subTitle, content } = props

  const { removeSnackbar } = useSnackbar()
  const isMobile = useWindowResize()

  const snackbarVariants = {
    hidden: { opacity: 0, y: isMobile ? -20 : 50 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -50 },
  }

  const iconSize = isMobile ? 24 : 20

  const iconColor: Record<SnackbarType, TextColorType> = {
    success: 'text-success-med-em',
    info: 'text-info-med-em',
    warning: 'text-warning-high-em',
    error: 'text-danger-med-em',
    generic: 'text-danger-med-em',
  }

  const buttonType: Record<SnackbarType, ButtonProps['variant']> = {
    success: 'success',
    info: 'info',
    warning: 'warning',
    error: 'danger',
    generic: 'secondary',
  }

  return (
    <motion.div
      id={id}
      className={classNames(styles.snackbar, {
        [styles[type]]: true,
        [styles.hasSubtitle]: subTitle,
        [styles.noSubtitle]: !subTitle,
      })}
      variants={snackbarVariants}
      initial='hidden'
      animate='visible'
      exit='exit'
      layout
    >
      <div className={styles.leftWrapper}>
        <InfoFilledIcon
          size={iconSize}
          color={iconColor[type]}
          secondaryColor='text-inverse-black-neutral'
        />
        <div className={styles.messageWrapper}>
          <div
            className={classNames(styles.messageBody, {
              [styles.hasSubtitle]: subTitle,
            })}
          >
            <Title2 medium className={styles.messageText}>
              {content ?? message}
            </Title2>
            {subTitle && <Body2 className={styles.subTitle}>{subTitle}</Body2>}
          </div>
          {action && (
            <Button variant={buttonType[type]} onClick={action?.onClick}>
              {action.label}
            </Button>
          )}
        </div>
      </div>
      {closable && (
        <div className={styles.closeButton}>
          <IconButton.Ghost size='sm' icon={CloseIcon} onClick={() => removeSnackbar(id)} />
        </div>
      )}
    </motion.div>
  )
}

export const SnackbarContainer = (): JSX.Element => {
  const { snackbars } = useSnackbar()

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
    },
  }

  return (
    <motion.div
      className={styles.snackbarContainer}
      variants={containerVariants}
      animate='show'
      initial='hidden'
    >
      <AnimatePresence>
        {snackbars.map((snackbar) => (
          <Snackbar key={snackbar.id} {...snackbar} />
        ))}
      </AnimatePresence>
    </motion.div>
  )
}
